import { Link } from "react-router-dom";
import { ShoppingCart, Star, Truck } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/context/LanguageContext";
import WishlistButton from "@/components/WishlistButton";

export default function ProductCard({ product }) {
  const { add } = useCart();
  const { t, lang } = useLang();
  // Prefer localized name when visitor is not on IT.
  // DE → name_de, fallback name_en, fallback IT. Others → name_en, fallback IT.
  const displayName = lang === "it"
    ? product.name
    : lang === "de"
      ? (product.name_de || product.name_en || product.name)
      : (product.name_en || product.name);
  const badgeMap = {
    sale: { label: t("badge_sale"), cls: "badge-sale" },
    new: { label: t("badge_new"), cls: "badge-new" },
    best_seller: { label: t("badge_best"), cls: "badge-best" },
  };
  const badge = badgeMap[product.badge];
  const hasVariants = (product.variants || []).length > 1;
  const availablePrices = (product.variants || []).filter((v) => v.price > 0).map((v) => v.price);
  const displayPrice = hasVariants && availablePrices.length
    ? Math.min(...availablePrices)
    : product.price;
  const discount = product.compare_at_price && product.compare_at_price > displayPrice
    ? Math.round(100 - (displayPrice / product.compare_at_price) * 100)
    : null;

  return (
    <div data-testid={`product-card-${product.id}`} className="product-card bg-white rounded-xl border border-xd-border overflow-hidden flex flex-col group relative">
      <Link to={`/prodotto/${product.shopify_handle || product.id}`} className="block relative aspect-[4/3] bg-slate-50 overflow-hidden">
        {product.images?.[0] ? (
          <img src={product.images[0]} alt={`${displayName} - ${product.brand} ${product.model}`} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-sm">no image</div>
        )}
        {badge && (
          <span className={`absolute top-3 left-3 px-2.5 py-1 text-[10px] font-semibold rounded-md uppercase tracking-wide ${badge.cls}`}>
            {badge.label}
          </span>
        )}
        {discount && (
          <span className="absolute bottom-3 right-3 px-2 py-1 text-[10px] font-bold rounded-md bg-xd-orange text-white">
            -{discount}%
          </span>
        )}
      </Link>
      <WishlistButton productId={product.id} />

      <div className="p-4 flex flex-col flex-1">
        <div className="font-mono-tech text-[10px] uppercase tracking-widest text-xd-muted mb-1">
          {product.brand} · {product.model}
        </div>
        <Link to={`/prodotto/${product.shopify_handle || product.id}`} className="font-display text-[15px] leading-tight text-xd-text hover:text-xd-cyanHover line-clamp-2 min-h-[38px]">
          {displayName}
        </Link>

        <div className="flex items-center gap-1 mt-2 text-xs text-xd-muted">
          <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
          <span className="font-semibold text-xd-text">{product.rating?.toFixed(1)}</span>
          <span>({product.reviews_count})</span>
          <span className="mx-1">·</span>
          <span className="font-mono-tech">{product.screen_size}</span>
        </div>

        <div className="mt-3 flex items-end justify-between">
          <div>
            <div className="text-lg font-display font-semibold text-xd-text">
              {hasVariants && <span className="text-xs text-xd-muted font-normal mr-1">{t("card_price_from")}</span>}
              € {displayPrice.toFixed(2)}
            </div>
            {product.compare_at_price && product.compare_at_price > displayPrice && (
              <div className="text-xs text-xd-muted line-through">€ {product.compare_at_price.toFixed(2)}</div>
            )}
            <div className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-xd-success" data-testid={`card-shipping-badge-${product.id}`}>
              <Truck className="w-3 h-3" />
              <span>{lang === "it" ? t("ship_free_badge") : t("ship_eu_badge")}</span>
            </div>
          </div>
          {hasVariants ? (
            <Link
              to={`/prodotto/${product.shopify_handle || product.id}`}
              data-testid={`add-to-cart-${product.id}`}
              className="btn-cta rounded-lg h-9 px-3 text-xs font-semibold flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-xd-orange/50"
              aria-label={t("card_choose_variant")}
            >
              {t("card_choose")}
            </Link>
          ) : (
            <button
              type="button"
              data-testid={`add-to-cart-${product.id}`}
              onClick={() => add(product)}
              className="btn-cta rounded-lg h-9 w-9 flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-xd-orange/50"
              aria-label={t("add_to_cart")}
            >
              <ShoppingCart className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
